import { Menu } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import { Dispatch } from '@reduxjs/toolkit';
import { DialogVariantEnum } from 'enums/dialogVariant';
import { MenuItemsEnum } from 'enums/menuItemsEnum';
import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { AppDispatch, AppState } from 'store';
import { getAllowDueDateChangeSelector } from 'store/auth/auth.selectors';
import { dialogOpen } from 'store/dialog/dialog.actions';
import { IDialogOpenPayload } from 'store/dialog/dialog.interface';
import { performOperation } from 'store/jobs/jobs.actions';
import { getRequiredJobStatuses } from 'utils/getRequiredJobStatuses';
import { MassOperationRequest, XTMJobStatus } from 'utils/restApplicationClient';
import QuickActionMenuItem from './QuickActionMenuItem';

interface IProps {
  anchorElement: null | HTMLElement;
  handleClose: () => void;
  currentMenuItem: MenuItemsEnum;
  projectInfo: IDialogOpenPayload['projectInfo'];
}

interface IState {
  menuAnchorElement: null | HTMLElement;
  menuItems: Array<{
    variant: DialogVariantEnum;
    requiredMenuItems: Array<MenuItemsEnum>;
  }>;
}

interface IDispatchProps {
  dialogOpen: (payload: IDialogOpenPayload) => AppDispatch;
  performOperation: (payload: MassOperationRequest) => AppDispatch;
}

interface IStateProps {
  allowDueDateChange: boolean;
}

type PropType = IProps & IDispatchProps & IStateProps;

class QuickActionMenu extends Component<PropType, IState> {
  constructor(props: PropType) {
    super(props);
    this.state = {
      menuAnchorElement: null,
      menuItems: [],
    };
  }

  componentDidMount(): void {
    this.setState({ menuItems: this.getMenuItems() });
  }

  componentDidUpdate(previousProps: PropType): void {
    const { allowDueDateChange } = this.props;

    if (previousProps.allowDueDateChange !== allowDueDateChange) {
      this.setState({ menuItems: this.getMenuItems() });
    }
  }

  getMenuItems = (): IState['menuItems'] => {
    const { allowDueDateChange } = this.props;
    const menuItems = [
      { variant: DialogVariantEnum.IMPORT, requiredMenuItems: [MenuItemsEnum.PROJECT, MenuItemsEnum.JOB] },
      { variant: DialogVariantEnum.CANCEL, requiredMenuItems: [MenuItemsEnum.PROJECT, MenuItemsEnum.JOB] },
      { variant: DialogVariantEnum.DELETE, requiredMenuItems: [MenuItemsEnum.PROJECT, MenuItemsEnum.JOB] },
      { variant: DialogVariantEnum.SEND, requiredMenuItems: [MenuItemsEnum.PROJECT] },
      { variant: DialogVariantEnum.REMOVE_LANGUAGE, requiredMenuItems: [MenuItemsEnum.LANGUAGE] },
    ];

    if (allowDueDateChange) {
      menuItems.push(
        { variant: DialogVariantEnum.CHANGE_DUE_DATE_PROJECT, requiredMenuItems: [MenuItemsEnum.PROJECT] },
        { variant: DialogVariantEnum.CHANGE_DUE_DATE_JOB, requiredMenuItems: [MenuItemsEnum.JOB] },
      );
    }

    return menuItems;
  };

  handleLanguagesMenuOpen = (event: React.MouseEvent<HTMLElement>): void => {
    this.setState({
      menuAnchorElement: event.currentTarget,
    });
  };

  handleLanguagesMenuClose = (): void => {
    this.setState({
      menuAnchorElement: null,
    });
  };

  checkIfStatusIsViable = (variant: DialogVariantEnum): boolean => {
    const { projectInfo } = this.props;
    const requiredStatuses = getRequiredJobStatuses(variant);
    if (projectInfo?.jobs) {
      const jobStatuses = new Set(projectInfo.jobs.map(({ status }) => status));
      // eslint-disable-next-line unicorn/prefer-spread
      return Array.from(jobStatuses).findIndex((status) => requiredStatuses.includes(status as XTMJobStatus)) >= 0;
    }
    return true;
  };

  handleClick = (variant: DialogVariantEnum) => (): void => {
    const { dialogOpen, handleClose, projectInfo } = this.props;

    this.handleLanguagesMenuClose();
    handleClose();

    if (!this.checkIfStatusIsViable(variant)) {
      dialogOpen({
        variant: DialogVariantEnum.NO_STATUS,
        action: DialogVariantEnum.NO_STATUS,
      });
      return;
    }

    dialogOpen({
      variant,
      action: variant,
      projectInfo,
    });
  };

  render(): JSX.Element {
    const { anchorElement, handleClose, currentMenuItem, projectInfo } = this.props;
    const { menuAnchorElement, menuItems } = this.state;

    return (
      <Fragment>
        <Menu
          open={!!menuAnchorElement}
          anchorEl={menuAnchorElement}
          onClose={this.handleLanguagesMenuClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <QuickActionMenuItem
            disabled={projectInfo?.joinedFilesInXTM || false}
            handleClick={this.handleClick(DialogVariantEnum.ADD_LANGUAGES)}
            label="queue.labels.add"
            displayTooltip={projectInfo?.joinedFilesInXTM || false}
            tooltipMessageCode="queue.fileJoining.addLanguage"
          />
          <QuickActionMenuItem
            handleClick={this.handleClick(DialogVariantEnum.REMOVE_LANGUAGES)}
            label="queue.labels.remove"
          />
        </Menu>
        <Menu open={!!anchorElement} anchorEl={anchorElement} onClose={handleClose}>
          <QuickActionMenuItem
            currentMenuItem={currentMenuItem}
            label="queue.labels.languages"
            handleClick={this.handleLanguagesMenuOpen}
            requiredMenuItems={[MenuItemsEnum.PROJECT]}
            iconComponent={<ChevronLeftIcon fontSize="small" />}
          />
          {menuItems.map(({ variant, requiredMenuItems }, index) => (
            <QuickActionMenuItem
              handleClick={this.handleClick(variant)}
              label={`queue.labels.${variant}`}
              currentMenuItem={currentMenuItem}
              requiredMenuItems={requiredMenuItems}
              disabled={!this.checkIfStatusIsViable(variant)}
              key={index}
            />
          ))}
        </Menu>
      </Fragment>
    );
  }
}

const mapStateToProps = (state: AppState): IStateProps => ({
  allowDueDateChange: getAllowDueDateChangeSelector(state),
});

const mapDispatchToProps = (dispatch: Dispatch<AppDispatch>): IDispatchProps => ({
  dialogOpen: (payload: IDialogOpenPayload): AppDispatch => dispatch(dialogOpen(payload)),
  performOperation: (payload: MassOperationRequest): AppDispatch => dispatch(performOperation(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(QuickActionMenu);
